import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCampaignConfigService } from '../services';
import { setConfigFavorite } from '../slices';
import { CampaignConfigView } from '../types';

export const useToggleCampaignConfigFavorite = () => {
  const dispatch = useDispatch();
  const currentCompanyId = useSelector(getCurrentCompanyId);
  const { setCampaignConfigFavorite, unsetCampaignConfigFavorite } = useCampaignConfigService();
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const toggleFavorite = useCallback(async (config: CampaignConfigView) => {
    if (!currentCompanyId) {
      toast.error('Unable to update favorites: no company selected');
      return;
    }

    // Ignore repeated clicks while a request for this config is in flight
    if (pendingIds.has(config.id)) {
      return;
    }

    const previous = config.favorite;
    const next = !previous;

    setPendingIds(prev => new Set(prev).add(config.id));

    // Optimistic update
    dispatch(setConfigFavorite({ id: config.id, favorite: next }));

    try {
      if (next) {
        await setCampaignConfigFavorite(config.id, currentCompanyId);
      } else {
        await unsetCampaignConfigFavorite(config.id, currentCompanyId);
      }
    } catch (error) {
      // Roll back on failure
      dispatch(setConfigFavorite({ id: config.id, favorite: previous }));
      toast.error('Failed to update favorites');
      console.error('Failed to toggle campaign config favorite:', error);
    } finally {
      setPendingIds(prev => {
        const updated = new Set(prev);
        updated.delete(config.id);
        return updated;
      });
    }
  }, [currentCompanyId, dispatch, pendingIds, setCampaignConfigFavorite, unsetCampaignConfigFavorite]);

  return {
    toggleFavorite,
    pendingIds,
  };
};
